import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { AppService } from '@src/suite/app.service';
import { RSSFeedViewStateRecord } from '@src/suite/common/data-models/records/rss-feed-viewstate-record';
import { TwitterFeedViewStateRecord } from '@src/suite/common/data-models/records/twitter-feed-viewstate-record';
import { WireViewStateRecord } from '@src/suite/common/data-models/records/wire-viewstate-record';
import { ViewStateTypes, ViewStateTypesEnum } from '@src/suite/common/enums/viewstate-types';
import { GlobalTablesService } from '@src/suite/common/services/global-tables-service';
import { LayoutState } from '@src/suite/common/services/layout-state';
import { PublicationChannelsService } from '@src/suite/common/services/publication-channels-service';
import { CastApiActions } from '@src/suite/common/store/cast/cast.actions';
import { selectGroupSort, selectPSTGroupPrivileges } from '@src/suite/common/store/global-tables/global-tables.selectors';
import { selectI18N } from '@src/suite/common/store/i18n/selectors/i18n.selectors';
import { ApplicationActions } from '@src/suite/common/store/navigation/actions/application.actions';
import { NavigationApiActions } from '@src/suite/common/store/navigation/actions/navigation.actions';
import { selectCurrentApplication } from '@src/suite/common/store/navigation/selectors/navigation.selectors';
import { getGroupByOptions, getLayoutOptions, getSortByOptions } from '@src/suite/common/utils/viewstate-utils';
import { FeedProviderApiActions } from '@src/suite/discover/store/actions/feed.provider.actions';
import { selectTwitterCredentials } from '@src/suite/discover/store/selectors/feed.discover.selectors';
import { catchError, exhaustMap, from, map, mergeMap, of, switchMap } from 'rxjs';

import { CreateViewStateActions } from './createviewstate.actions';
import { selectCreateViewStateData } from './createviewstate.selectors';

@Injectable()
export class CreateViewStateEffects {
    private actions$ = inject(Actions);
    private store = inject(Store);
    private viewStateService = inject(LayoutState);
    private publicationChannelsService = inject(PublicationChannelsService);
    private globalTablesService = inject(GlobalTablesService);
    private appService = inject(AppService);

    updateViewStateType$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(CreateViewStateActions.updateViewStateType),
            concatLatestFrom(() => this.store.select(selectCreateViewStateData)),
            map(([action, state]) => {
                const title = state.viewState.title || '';
                return CreateViewStateActions.initializeViewState({
                    vstype: action.vstype,
                    copy: false,
                    edit: false,
                    vs: null,
                    group: state.selectedGroup,
                    section: state.selectedSection,
                    item: state.selectedItem,
                    title: title,
                });
            })
        );
    });

    initializeViewState$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(CreateViewStateActions.initializeViewState),
            concatLatestFrom(() => [this.store.select(selectCurrentApplication), this.store.select(selectI18N), this.store.select(selectPSTGroupPrivileges), this.store.select(selectGroupSort)]),
            mergeMap(([action, application, i18n, PSTGroupPrivs, groupSort]) => {
                const layout = getLayoutOptions(action.vstype, i18n, PSTGroupPrivs);
                const groupby = getGroupByOptions(action.vstype, groupSort, i18n);
                const sortby = getSortByOptions(action.vstype, groupSort, i18n);
                if (application === 'playbook' || application === 'live') {
                    return of(
                        CreateViewStateActions.initializeViewStateSuccess({
                            vstype: action.vstype,
                            copy: action.copy,
                            edit: action.edit,
                            vs: action.vs,
                            group: action.group,
                            section: action.section,
                            item: action.item,
                            layout: layout,
                            groupby: groupby,
                            sortby: sortby,
                            title: action.title,
                        })
                    );
                }
                if (application === 'cast') {
                    return from(
                        this.viewStateService.getViewStateRecords('playbook', undefined, this.globalTablesService.PlaybookTeamsArray.find(x => x.ID === this.appService.nomWebServicesJson.PSTGroup).ID)
                    ).pipe(
                        exhaustMap(_responses => {
                            const actions = [];
                            actions.push(CastApiActions.loadPublicationsChannels());
                            actions.push(
                                CreateViewStateActions.loadStoredViewStates({
                                    vsType: action.vstype,
                                })
                            );
                            actions.push(
                                CreateViewStateActions.initializeViewStateSuccess({
                                    vstype: action.vstype,
                                    copy: action.copy,
                                    edit: action.edit,
                                    vs: action.vs,
                                    group: action.group,
                                    section: action.section,
                                    item: action.item,
                                    layout: layout,
                                    groupby: groupby,
                                    sortby: sortby,
                                    title: action.title,
                                })
                            );

                            return actions;
                        }),
                        catchError(error => {
                            return of(CreateViewStateActions.initializeViewStateError({ error: error }));
                        })
                    );
                }
                if (application === 'discover') {
                    return from(this.viewStateService.loadViewStateRecords('all', null, 'discover')).pipe(
                        mergeMap(() => {
                            return [
                                //FeedProviderApiActions.getWireFeeds(),
                                //FeedProviderApiActions.getAccessTokens(),
                                FeedProviderApiActions.getCredentialsAndFeeds(),
                                CreateViewStateActions.initializeViewStateSuccess({
                                    vstype: action.vstype,
                                    copy: action.copy,
                                    edit: action.edit,
                                    vs: action.vs,
                                    group: action.group,
                                    section: action.section,
                                    item: action.item,
                                    layout: layout,
                                    groupby: groupby,
                                    sortby: sortby,
                                    title: action.title,
                                }),
                            ];
                        }),
                        catchError(error => of(CreateViewStateActions.initializeViewStateError({ error })))
                    );
                }

                return of(ApplicationActions.noOpAction());
            })
        );
    });

    saveViewState$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(CreateViewStateActions.saveViewState),
            concatLatestFrom(() => [this.store.select(selectCurrentApplication), this.store.select(selectCreateViewStateData), this.store.select(selectTwitterCredentials)]),
            mergeMap(([_, application, state, twitter]) => {
                if (application === 'discover') {
                    const viewState = state.viewState;
                    if (!state.inEditMode) {
                        if (viewState.viewStateType === ViewStateTypes.RSSFEED) {
                            const vs = <RSSFeedViewStateRecord>viewState;

                            vs.viewStateDefaultLayout = vs.viewStateLayout.Value;
                            vs.viewStateDefaultSort = vs.viewStateSorting.Value;
                            vs.viewStateScope = 0;
                            vs.viewStateType = ViewStateTypes.RSSFEED;

                            return from(this.viewStateService.createRSSFeed(vs)).pipe(
                                map(() => CreateViewStateActions.saveViewStateSuccess({ vs: vs })),
                                catchError(error => of(CreateViewStateActions.saveViewStateError({ error })))
                            );
                        }

                        if (viewState.viewStateType === ViewStateTypes.TWITTERFEED && twitter.length > 0) {
                            const vs = <TwitterFeedViewStateRecord>viewState;
                            // @TODO KAREV
                            //vs.viewStateFeedNames = JSON.stringify(this.twitterUsernames);
                            vs.viewStateScope = 0;
                            vs.viewStateDefaultLayout = vs.viewStateLayout.Value;
                            vs.viewStateDefaultSort = vs.viewStateSorting.Value;
                            vs.viewStateType = ViewStateTypes.TWITTERFEED;

                            // Create Twitter feed
                            return from(this.viewStateService.createTwitterFeed(vs, twitter[0]?.wireCredentialsId)).pipe(
                                map(() => CreateViewStateActions.saveViewStateSuccess({ vs: vs })),
                                catchError(error => of(CreateViewStateActions.saveViewStateError({ error })))
                            );
                        }
                        if (viewState.viewStateType === ViewStateTypes.APFEED || viewState.viewStateType === ViewStateTypes.FTPFEED) {
                            const vs = <WireViewStateRecord>viewState;
                            vs.viewStateScope = 0;
                            vs.viewStateDefaultLayout = vs.viewStateLayout.Value;
                            vs.viewStateDefaultSort = vs.viewStateSorting.Value;
                            vs.viewStateType = viewState.viewStateType;

                            return of(CreateViewStateActions.saveViewStateSuccess({ vs: vs }));
                        }
                        if (viewState.viewStateType === ViewStateTypes.MULTIPLEFEED) {
                            return of(CreateViewStateActions.saveViewStateSuccess({ vs: state.viewState }));
                        }
                    } else {
                        return of(CreateViewStateActions.saveViewStateSuccess({ vs: state.viewState }));
                    }
                }

                if (application === 'playbook' || application === 'cast' || application === 'live') {
                    return of(CreateViewStateActions.saveViewStateSuccess({ vs: state.viewState }));
                }

                return of(ApplicationActions.noOpAction());
            })
        );
    });

    saveViewStateSuccess$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(CreateViewStateActions.saveViewStateSuccess),
            concatLatestFrom(() => [this.store.select(selectCurrentApplication), this.store.select(selectCreateViewStateData)]),
            map(([_, _application, state]) => {
                if (state.inEditMode) {
                    return NavigationApiActions.updateNavigationItem({ vs: state.viewState });
                } else {
                    return NavigationApiActions.createNavigationItem({ vs: state.viewState });
                }
            })
        );
    });

    enterBrowseMode$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(CreateViewStateActions.enterBrowseMode),
            mergeMap(() => {
                return from(this.viewStateService.loadViewStateRecords('all', null, 'discover')).pipe(
                    map(() => CreateViewStateActions.enterBrowseModeSuccess()),
                    catchError(error => of(CreateViewStateActions.enterBrowseModeError({ error })))
                );
            })
        );
    });

    saveAddViewStates$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(CreateViewStateActions.saveAddViewStates),
            concatLatestFrom(() => [this.store.select(selectCreateViewStateData), this.store.select(selectTwitterCredentials)]),
            mergeMap(([_, state, twitter]) => {
                if (state.inConfigureMode) {
                    const viewState = state.viewState;
                    if (!state.inEditMode) {
                        if (viewState.viewStateType === ViewStateTypes.RSSFEED) {
                            const vs = <RSSFeedViewStateRecord>viewState;

                            vs.viewStateDefaultLayout = vs.viewStateLayout.Value;
                            vs.viewStateDefaultSort = vs.viewStateSorting.Value;
                            vs.viewStateScope = 0;
                            vs.viewStateType = ViewStateTypes.RSSFEED;

                            return from(this.viewStateService.createRSSFeed(vs)).pipe(
                                map(() => CreateViewStateActions.saveAddViewStatesSuccess()),
                                catchError(error => of(CreateViewStateActions.saveAddViewStatesError({ error })))
                            );
                        }

                        if (viewState.viewStateType === ViewStateTypes.TWITTERFEED && twitter?.length > 0) {
                            const vs = <TwitterFeedViewStateRecord>viewState;
                            // @TODO KAREV
                            //vs.viewStateFeedNames = JSON.stringify(this.twitterUsernames);
                            vs.viewStateScope = 0;
                            vs.viewStateDefaultLayout = vs.viewStateLayout.Value;
                            vs.viewStateDefaultSort = vs.viewStateSorting.Value;
                            vs.viewStateType = ViewStateTypes.TWITTERFEED;

                            // Create Twitter feed
                            return from(this.viewStateService.createTwitterFeed(vs, twitter[0]?.wireCredentialsId)).pipe(
                                map(() => CreateViewStateActions.saveAddViewStatesSuccess()),
                                catchError(error => of(CreateViewStateActions.saveAddViewStatesError({ error })))
                            );
                        }
                        if (viewState.viewStateType === ViewStateTypes.WIREFEED) {
                            const vs = <WireViewStateRecord>viewState;
                            vs.viewStateScope = 0;
                            vs.viewStateDefaultLayout = vs.viewStateLayout.Value;
                            vs.viewStateDefaultSort = vs.viewStateSorting.Value;
                            vs.viewStateType = ViewStateTypes.WIREFEED;

                            return from(this.viewStateService.createViewStateRecord(vs)).pipe(
                                map(() => CreateViewStateActions.saveAddViewStatesSuccess()),
                                catchError(error => of(CreateViewStateActions.saveAddViewStatesError({ error })))
                            );
                        }
                    }
                }
                if (state.inBrowseMode) {
                    return of(CreateViewStateActions.saveAddViewStatesSuccess());
                }
                return of(ApplicationActions.noOpAction());
            })
        );
    });

    loadStoredViewStates$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(CreateViewStateActions.loadStoredViewStates),
            switchMap(({ vsType }) => {
                return from(this.viewStateService.getViewStateRecords('playbook', null, 'group')).pipe(
                    map(({ data }) => data?.views),
                    map(views => {
                        return CreateViewStateActions.setStoredViewStates({
                            viewStates:
                                vsType === ViewStateTypesEnum.PUBLICATION ? views?.filter(vs => vs.viewStateType === ViewStateTypes.TOPIC || vs.viewStateType === ViewStateTypes.ASSIGNMENT) : views,
                        });
                    }),
                    catchError(() => of(ApplicationActions.noOpAction()))
                );
            })
        );
    });
}
